import React, { useMemo, useState } from 'react';
import formatMoney from '@/lib/formatMoney';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  Brush,
} from 'recharts';

/* ── Custom tooltip with full details ── */
function EquityTooltipContent({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  const equity = point.equity;
  const bh = point.buyAndHold;
  const dd = point.drawdown;
  const pnl = point.pnl;
  const pnlPct = point.pnlPct;

  return (
    <div className="bg-[#18181b] border border-[#374151] rounded-lg px-3 py-2 shadow-xl text-[11px]">
      <p className="text-[#9ca3af] text-[10px] mb-1">{point.date}</p>
      <div className="space-y-0.5">
        <div className="flex justify-between gap-4">
          <span className="text-[#9ca3af]">Strategy</span>
          <span className="text-[#e5e7eb] font-medium">${Number(equity).toLocaleString()}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-[#9ca3af]">P&L</span>
          <span className={pnl >= 0 ? 'text-green-400' : 'text-red-400'}>
            {formatMoney(pnl, { sign: true })} ({pnlPct.toFixed(2)}%)
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-[#9ca3af]">Buy & Hold</span>
          <span className="text-[#e5e7eb]">${Number(bh).toLocaleString()}</span>
        </div>
        {dd != null && dd !== 0 && (
          <div className="flex justify-between gap-4">
            <span className="text-[#9ca3af]">Drawdown</span>
            <span className="text-red-400">{dd.toFixed(2)}%</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function EquityChart({ equityCurve, drawdownCurve, bars, initialCapital }) {
  const data = useMemo(() => {
    if (!equityCurve?.length || !bars?.length) return [];
    const startPrice = bars[0].close;
    return equityCurve.map((pt) => {
      const bar = bars[pt.barIndex];
      const bh = bar ? initialCapital * (bar.close / startPrice) : initialCapital;
      const dd = drawdownCurve?.find((d) => d.barIndex === pt.barIndex);
      const pnl = pt.equity - initialCapital;
      return {
        date: bar?.timestamp ? new Date(bar.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '',
        equity: pt.equity,
        buyAndHold: Math.round(bh * 100) / 100,
        drawdown: dd ? -dd.drawdown : 0,
        pnl: Math.round(pnl * 100) / 100,
        pnlPct: (pnl / initialCapital) * 100,
      };
    });
  }, [equityCurve, drawdownCurve, bars, initialCapital]);

  if (!data.length) return null;

  const current = data[data.length - 1];
  const pnl = current.equity - initialCapital;
  const pnlClass = pnl >= 0 ? 'text-green-400' : 'text-red-400';
  const minDD = Math.min(...data.map((d) => d.drawdown));

  // Show brush for panning when dataset is large enough
  const showBrush = data.length > 60;

  return (
    <div className="flex flex-col h-full">
      {/* Equity headline */}
      <div className="flex items-baseline gap-3 px-2 pb-1.5 flex-shrink-0">
        <span className={`text-lg font-bold ${pnlClass}`}>
          {formatMoney(current.equity)}
        </span>
        <span className={`text-xs font-medium ${pnlClass}`}>
          {formatMoney(pnl, { sign: true })} ({((pnl / initialCapital) * 100).toFixed(2)}%)
        </span>
        <span className="text-[10px] text-muted-foreground/50 ml-auto">
          DD {minDD.toFixed(1)}%
        </span>
      </div>

      {/* Equity curve */}
      <div className="flex-1 min-h-0" style={{ minHeight: 100 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 4, bottom: showBrush ? 2 : 0, left: 4 }}>
            <defs>
              <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={pnl >= 0 ? '#22c55e' : '#ef4444'} stopOpacity={0.3} />
                <stop offset="95%" stopColor={pnl >= 0 ? '#22c55e' : '#ef4444'} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" hide />
            <YAxis domain={['auto', 'auto']} hide />
            <Tooltip content={<EquityTooltipContent />} />
            <ReferenceLine y={initialCapital} stroke="#6b7280" strokeDasharray="3 3" strokeWidth={0.5} />
            <Area
              type="monotone"
              dataKey="equity"
              stroke={pnl >= 0 ? '#22c55e' : '#ef4444'}
              strokeWidth={1.5}
              fill="url(#eqGrad)"
              dot={false}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="buyAndHold"
              stroke="#6b7280"
              strokeWidth={1}
              strokeDasharray="4 3"
              fill="none"
              dot={false}
              isAnimationActive={false}
            />
            {showBrush && (
              <Brush
                dataKey="date"
                height={16}
                stroke="#374151"
                fill="#0a0a0a"
                travellerWidth={8}
                tickFormatter={() => ''}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
